// Full updated Review.jsx
import "./Review.css";
import hall from "../../newsImage/hall.jpg";
import series from "../../newsImage/series.jpg";
import { useEffect, useState, useRef } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import Skeleton from "../../../../components/Skeleton";
import like from "../../newsImage/like.png";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import worldCup from "../../../home/HomeImage/worldcup.png";

const Review = ({ dark, count = 1, user, MyBaseUrl }) => {
  const { slug } = useParams();

  const textareaRef = useRef(null);

  const createSlug = (homeTeam, awayTeam, name) => {
    return [homeTeam, awayTeam, name]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");
  };

  const location = useLocation();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [moreQuest, setMoreQuest] = useState([]);
  const [selectedQuest, setSelectedQuest] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showResult, setShowResult] = useState({});
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);

  const [showLoginModal, setShowLoginModal] = useState(false);

  const [commentModal, setCommentModal] = useState(false);

  const [replies, setReplies] = useState({});
  const [likedComments, setLikedComments] = useState({});

  const [replyingTo, setReplyingTo] = useState(null);

  const [visibleReplies, setVisibleReplies] = useState({});

  const [commentPage, setCommentPage] = useState(1);
  const [commentLastPage, setCommentLastPage] = useState(1);
  const [visiblePageLimit, setVisiblePageLimit] = useState(3);
  const [commentLoading, setCommentLoading] = useState(false);
  const [openReplyBox, setOpenReplyBox] = useState(true);

  const [closingModal, setClosingModal] = useState(false);

  const [totalComments, setTotalComments] = useState(0);

  const modalSheetRef = useRef(null);

  const [draggingSheet, setDraggingSheet] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragOffsetY, setDragOffsetY] = useState(0);

  const createSafeHTML = (html) => {
    return DOMPurify.sanitize(html || "", {
      ALLOWED_TAGS: [
        "p",
        "b",
        "i",
        "em",
        "strong",
        "a",
        "ul",
        "ol",
        "li",
        "br",
        "img",
        "h1",
        "h2",
        "h3",
        "h4",
        "blockquote",
      ],
      ALLOWED_ATTR: ["href", "src", "alt", "title", "style"],
    });
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diff = Math.floor((now - date) / 1000);

    const mins = Math.floor(diff / 60);
    const hrs = Math.floor(diff / 3600);
    const days = Math.floor(diff / 86400);

    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}hr ago`;

    return `${days}d ago`;
  };

  const getStoredReplies = () => {
    try {
      return JSON.parse(localStorage.getItem(`replies_${slug}`)) || {};
    } catch {
      return {};
    }
  };

  const saveStoredReplies = (data) => {
    localStorage.setItem(`replies_${slug}`, JSON.stringify(data));
  };

  const getLiked = () => {
    try {
      return JSON.parse(localStorage.getItem("likedComments")) || {};
    } catch {
      return {};
    }
  };

  const saveLiked = (data) => {
    localStorage.setItem("likedComments", JSON.stringify(data));
  };

  const fetchComments = async (page = 1, append = false, postId = post?.id) => {
    if (!postId) return;

    try {
      setCommentLoading(true);

      const res = await fetch(
        `${MyBaseUrl}/posts/${postId}/comments?page=${page}`,
      );

      const commentData = await res.json();

      const liked = getLiked();

      const formatted = (commentData?.data?.data || []).map((item) => ({
        ...item,
        liked_by_user: !!liked[item.id],
        likes_count: item.likes_count || 0,
      }));

      if (append) {
        setComments((prev) => [...prev, ...formatted]);
      } else {
        setComments(formatted);
      }

      setCommentPage(commentData?.data?.meta?.current_page || 1);
      setCommentLastPage(commentData?.data?.meta?.last_page || 1);
      setTotalComments(commentData?.data?.meta?.total || 0);
    } catch (error) {
      console.log(error);
    } finally {
      setCommentLoading(false);
    }
  };

  useEffect(() => {
    if (!slug) return;

    setReplies(getStoredReplies());
    setLikedComments(getLiked());
  }, [slug]);

  const CACHE_KEY = `newsData_${slug}`;

  const isPostCached = () => {
    const cached = localStorage.getItem(CACHE_KEY);

    if (!cached) return false;

    try {
      const parsed = JSON.parse(cached);
      return !!parsed?.post?.id;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!slug) return;

    const cached = localStorage.getItem(`newsData_${slug}`);
    const hasCache = isPostCached();

    if (hasCache) {
      const parsed = JSON.parse(cached);

      setPost(parsed.post);
      setRelatedPosts(parsed.relatedPosts || []);
      setLoading(false);

      fetchComments(1, false, parsed.post.id);
    } else {
      setLoading(true);
      setPost(null);
      setRelatedPosts([]);
      setComments([]);
      setTotalComments(0);
    }

    // fetch fresh version in background
    fetch(`${MyBaseUrl}/posts/${slug}`)
      .then((res) => res.json())
      .then((data) => {
        const mainPost = data.data.post;
        const related = data.data.related_posts;

        setPost(mainPost);
        setRelatedPosts(related);

        localStorage.setItem(
          `newsData_${slug}`,
          JSON.stringify({
            post: mainPost,
            relatedPosts: related,
          }),
        );

        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug]);

  const showLoader = loading && !isPostCached() && !post;

  useEffect(() => {
    if (post?.id) {
      fetchComments(1, false, post.id);
    }
  }, [post?.id]);

  const loadNextPage = () => {
    if (commentPage < commentLastPage) {
      fetchComments(commentPage + 1, true);
    }
  };

  const handleSeeMorePages = () => {
    setVisiblePageLimit((prev) => prev + 3);
  };

  const handleLike = async (comment) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    const alreadyLiked = comment.liked_by_user;

    const map = getLiked();

    setComments((prev) =>
      prev.map((item) =>
        item.id === comment.id
          ? {
              ...item,
              liked_by_user: !alreadyLiked,
              likes_count: alreadyLiked
                ? Math.max((item.likes_count || 0) - 1, 0)
                : (item.likes_count || 0) + 1,
            }
          : item,
      ),
    );

    if (alreadyLiked) {
      delete map[comment.id];
    } else {
      map[comment.id] = true;
    }

    saveLiked(map);

    try {
      const response = await fetch(
        `${MyBaseUrl}/user/comments/${comment.id}/like`,
        {
          method: alreadyLiked ? "DELETE" : "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        },
      );

      const data = await response.json();

      console.log(data);

      if (!response.ok) {
        throw new Error(data?.message || "Like request failed");
      }
    } catch (err) {
      console.log(err);

      setComments((prev) =>
        prev.map((item) =>
          item.id === comment.id
            ? {
                ...item,
                liked_by_user: alreadyLiked,
                likes_count: alreadyLiked
                  ? (item.likes_count || 0) + 1
                  : Math.max((item.likes_count || 0) - 1, 0),
              }
            : item,
        ),
      );

      if (alreadyLiked) {
        map[comment.id] = true;
      } else {
        delete map[comment.id];
      }

      saveLiked(map);
    }
  };

  const openReply = (comment) => {
    setCommentModal(true);
    setReplyingTo(comment);

    setTimeout(() => {
      textareaRef.current?.focus();
    }, 250);
  };

  const handleCommentSubmit = async () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    if (!newComment.trim()) return;

    const text = newComment.trim();

    if (replyingTo) {
      const reply = {
        id: Date.now(),
        content: text,
        mention: replyingTo?.author?.name,
        created_at: new Date().toISOString(),
        author: {
          name: user?.name || "Anonymous",
          image: user?.avatar || hall,
        },
      };

      const saved = getStoredReplies();

      const updated = {
        ...saved,
        [replyingTo.id]: [reply, ...(saved[replyingTo.id] || [])],
      };

      saveStoredReplies(updated);
      setReplies(updated);

      setReplyingTo(null);
      setNewComment("");
      return;
    }

    const tempComment = {
      id: Date.now(),
      content: text,
      created_at: new Date().toISOString(),
      likes_count: 0,
      liked_by_user: false,
      author: {
        name: user?.name || "Anonymous",
        image: user?.avatar || hall,
      },
    };

    setComments((prev) => [tempComment, ...prev]);
    setNewComment("");

    try {
      setPosting(true);

      await fetch(`${MyBaseUrl}/posts/${post?.id}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify({
          content: text,
          name: user?.name,
        }),
      });
    } catch {
    } finally {
      setPosting(false);
    }
  };

  const closeCommentModal = () => {
    setClosingModal(true);

    setTimeout(() => {
      setCommentModal(false);
      setReplyingTo(null);
      setClosingModal(false);
    }, 580);
  };

  const startDragSheet = (clientY) => {
    setDraggingSheet(true);
    setDragStartY(clientY);
  };

  const moveDragSheet = (clientY) => {
    if (!draggingSheet) return;

    const diff = clientY - dragStartY;

    if (diff > 0) {
      setDragOffsetY(diff);
    }
  };

  const endDragSheet = () => {
    if (!draggingSheet) return;

    setDraggingSheet(false);

    if (dragOffsetY > 130) {
      closeCommentModal();
    } else {
      setDragOffsetY(0);
    }
  };

  const [pollLoading, setPollLoading] = useState(false);

  useEffect(() => {
    if (!slug) return;

    const fetchPoll = async () => {
      try {
        setPollLoading(true);

        const res = await fetch(`${MyBaseUrl}/polls/post/${slug}`);

        const data = await res.json();

        if (data?.data) {
          setMoreQuest([data.data]);
        } else {
          setMoreQuest([]);
        }
      } catch (err) {
        console.log(err);
        setMoreQuest([]);
      } finally {
        setPollLoading(false);
      }
    };

    fetchPoll();
  }, [slug]);

  const getPollVotes = () => {
    try {
      if (!user?.id) return {};

      const cookie = Cookies.get(`pollVotes_${user.id}`);

      return cookie ? JSON.parse(cookie) : {};
    } catch {
      return {};
    }
  };

  const savePollVotes = (data) => {
    if (!user?.id) return;

    Cookies.set(`pollVotes_${user.id}`, JSON.stringify(data), {
      expires: 365,
      secure: true,
      sameSite: "Lax",
    });
  };

  const getQuizAnswers = () => {
    try {
      if (!user?.id) return {};

      const cookie = Cookies.get(`quizAnswers_${slug}_${user.id}`);

      return cookie ? JSON.parse(cookie) : {};
    } catch {
      return {};
    }
  };

  const saveQuizAnswers = (data) => {
    if (!user?.id) return;

    Cookies.set(`quizAnswers_${slug}_${user.id}`, JSON.stringify(data), {
      expires: 365,
      secure: true,
      sameSite: "Lax",
    });
  };

  const handleSelectQuest = async (questionIndex, optionId) => {
    const poll = moreQuest[questionIndex];

    if (!user) {
      setShowLoginModal(true);
      return;
    }

    const storedVotes = getPollVotes();

    if (storedVotes[poll.id]) {
      return;
    }

    setSelectedQuest((prev) => ({
      ...prev,
      [questionIndex]: optionId,
    }));

    const updatedVotes = {
      ...storedVotes,
      [poll.id]: optionId,
    };

    savePollVotes(updatedVotes);

    setMoreQuest((prev) =>
      prev.map((q, i) => {
        if (i !== questionIndex) return q;

        return {
          ...q,
          user_has_voted: true,
          options: q.options.map((opt) =>
            opt.id === optionId
              ? {
                  ...opt,
                  votes_count: (opt.votes_count || 0) + 1,
                }
              : opt,
          ),
          total_votes: (q.total_votes || 0) + 1,
        };
      }),
    );

    try {
      setPollLoading(true);

      const response = await fetch(
        `${MyBaseUrl}/polls/${poll.id}/vote?poll_option_id=${optionId}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        },
      );

      const data = await response.json();

      console.log(data);

      if (!response.ok) {
        throw new Error(data?.message || "Failed to vote");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setPollLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.id || !moreQuest.length) {
      setSelectedQuest({});
      return;
    }

    const storedVotes = getPollVotes();

    const updated = {};

    moreQuest.forEach((poll, index) => {
      if (storedVotes[poll.id]) {
        updated[index] = storedVotes[poll.id];
      }
    });

    setSelectedQuest(updated);
  }, [moreQuest, user?.id]);

  const handleQuizSelect = (qIndex, aIndex, isCorrect, explanation) => {
    if (showResult[qIndex]) return;

    const updated = {
      ...quizAnswers,
      [qIndex]: {
        selected: aIndex,
        isCorrect,
        explanation,
      },
    };

    setQuizAnswers(updated);

    setShowResult((prev) => ({
      ...prev,
      [qIndex]: true,
    }));

    saveQuizAnswers(updated);
  };

  useEffect(() => {
    if (!slug || !user?.id) {
      setQuizAnswers({});
      setShowResult({});
      return;
    }

    const stored = getQuizAnswers();

    setQuizAnswers(stored);

    const resultMap = {};

    Object.keys(stored).forEach((key) => {
      resultMap[key] = true;
    });

    setShowResult(resultMap);
  }, [slug, user?.id]);

  const [quizLoaded, setQuizLoaded] = useState(false);

  useEffect(() => {
    if (slug) setQuizLoaded(true);
  }, [slug]);

  const [imageOrientation, setImageOrientation] = useState("landscape");

  const getInitials = (name = "") => {
    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join("");
  };

  return (
    <div className="homer">
      <Helmet>
        <title>
          {post?.title
            ? `${post.title} | News, Analysis & Comments`
            : "Sports News Review"}
        </title>

        <meta
          name="description"
          content={
            post?.excerpt ||
            post?.summary ||
            "Read the latest sports news, analysis, match reviews, polls, quizzes, and fan comments."
          }
        />

        <meta
          name="keywords"
          content={
            post?.tags?.length
              ? `${post.tags.map((t) => t.name).join(", ")}, sports news, football, match review, analysis, commentary`
              : "sports news, football review, match analysis, live commentary, polls, quizzes"
          }
        />

        <meta property="og:type" content="article" />
        <meta
          property="og:title"
          content={
            post?.title ? `${post.title} | Sports Review` : "Sports News Review"
          }
        />

        <meta
          property="og:description"
          content={
            post?.excerpt || "Read match reviews, analysis, and fan reactions."
          }
        />

        <meta
          property="og:image"
          content={
            post?.image ||
            "https://fadasports.com/static/media/mobilelogo.d0257d49e02d3094cce0.png"
          }
        />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={post?.title || "Sports News Review"}
        />
        <meta
          name="twitter:description"
          content={post?.excerpt || "Latest sports analysis and match reviews."}
        />
        <meta
          name="twitter:image"
          content={
            post?.image ||
            "https://fadasports.com/static/media/mobilelogo.d0257d49e02d3094cce0.png"
          }
        />
        <meta name="twitter:image" content={post?.image || ""} />
      </Helmet>

      {showLoader ? (
        <Skeleton dark={dark} count={6} />
      ) : (
        <div className="reviewWrapper">
          <div>
            <section className="mysec">
              <Link
                to={`/authors/${post?.author?.name
                  ?.trim()
                  .toLowerCase()
                  .replace(/[^a-z0-9\s]/g, "")
                  .replace(/\s+/g, "-")}`}
                dark={dark}
                className="aa">
                <span>
                  {post?.author?.avatar ? (
                    <img src={post.author.avatar} alt={post?.author?.name} />
                  ) : (
                    <div className="author-initials">
                      {getInitials(post?.author?.name)}
                    </div>
                  )}
                  <strong style={dark ? { color: "#d4d4d4ff" } : {}}>
                    {loading ? "" : `By ${post?.author?.name}`}
                    <strong style={dark ? { color: "#d4d4d4ff" } : {}}>
                      {loading
                        ? ""
                        : new Date(post?.published_at).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "2-digit",
                              year: "numeric",
                            },
                          )}
                    </strong>
                  </strong>
                </span>
              </Link>

              <div className="imgdiv3">
                <img
                  src={post?.image || hall}
                  alt=""
                  className={`responsive-post-image ${imageOrientation}`}
                  onLoad={(e) => {
                    const img = e.target;

                    setImageOrientation("landscape");
                  }}
                />
                <h1
                  style={
                    dark
                      ? {
                          backgroundColor: "#333",
                          color: "#fff",
                          boxShadow: "none",
                        }
                      : {}
                  }>
                  {loading ? "" : post?.title}
                </h1>
              </div>
              <section
                className="blur"
                style={
                  dark
                    ? { backgroundColor: "#333" }
                    : { backgroundColor: "white" }
                }>
                <span style={{ display: "flex" }} className="tags">
                  <nav>
                    {post?.tags?.map((tag, index) => (
                      <Link
                        to={
                          tag?.type === "team" || tag?.type === "nation"
                            ? `/teams/${createSlug(tag?.name)}-${tag?.entity_id}`
                            : tag?.type === "player"
                              ? `/players/${createSlug(tag?.name)}-${tag.entity_id}`
                              : `/competition/${tag?.name
                                  ?.toLowerCase()
                                  .replace(/[^a-z0-9\s-]/g, "")
                                  .trim()
                                  .replace(/\s+/g, "")}/${tag?.entity_id}`
                        }
                        key={index}
                        className="tagLink">
                        <h4
                          key={index}
                          style={{
                            border: "none",
                            boxShadow: dark
                              ? "0 0 10px 1px #333"
                              : "0 0 0 1px #ddd",
                            borderRadius: "8px",
                            padding: " 8px 12px",
                            color: dark ? "#d4d4d4ff" : "#161819",
                            backgroundColor: dark
                              ? "rgba(36, 41, 41, 1)"
                              : "#e6e6e63f",
                          }}>
                          <img
                            src={
                              tag?.entity_id === 1 &&
                              tag?.type === "competition"
                                ? worldCup
                                : tag?.icon || tag?.image
                            }
                            alt=""
                          />
                          <p style={{ color: dark ? "#d4d4d4ff" : "#161819" }}>
                            {tag?.name}
                          </p>
                        </h4>
                      </Link>
                    ))}
                  </nav>
                </span>

                <div
                  className="theContent"
                  style={dark ? { color: "#ddddddff" } : { color: "#161819" }}
                  dangerouslySetInnerHTML={{
                    __html: loading
                      ? ""
                      : createSafeHTML(post?.content || post?.excerpt || "")
                          .split(/\n\s*\n/)
                          .map((para) => `<p>${para}</p>`)
                          .join(""),
                  }}
                />

                {moreQuest.length > 0 && (
                  <section className="pollsection">
                    {moreQuest.map((quest, index) => {
                      const totalVotes =
                        quest.total_votes ||
                        quest.options.reduce(
                          (sum, item) => sum + (item.votes_count || 0),
                          0,
                        );

                      return (
                        <div key={index}>
                          <h2
                            style={
                              dark
                                ? { color: "#fafafaff" }
                                : { color: "#161819" }
                            }>
                            {quest.question}
                          </h2>

                          <nav>
                            {quest.options.map((option) => {
                              const total =
                                quest.options.reduce(
                                  (sum, item) => sum + (item.votes_count || 0),
                                  0,
                                ) || 1;

                              const percent = Math.round(
                                ((option.votes_count || 0) / total) * 100,
                              );

                              const userVote = selectedQuest[index] || null;

                              const hasUserVoted = !!selectedQuest[index];

                              const isSelected =
                                selectedQuest[index] === option.id;

                              return (
                                <span
                                  key={option.id}
                                  onClick={() => {
                                    if (hasUserVoted) return;

                                    handleSelectQuest(index, option.id);
                                  }}
                                  style={{
                                    display: "block",
                                    padding: !selectedQuest[index]
                                      ? "12px"
                                      : "5px 12px",
                                    marginBottom: "12px",
                                    borderRadius: "12px",
                                    transition: "all 0.35s ease",
                                    overflow: "hidden",
                                    background: isSelected
                                      ? "#F0DD49"
                                      : dark
                                        ? "#2c2c2cd0"
                                        : "#fafafaff",
                                    cursor: hasUserVoted
                                      ? "not-allowed"
                                      : "pointer",
                                  }}>
                                  <p
                                    style={{
                                      marginBottom: hasUserVoted
                                        ? "0px"
                                        : "0px",
                                      transition: "0.3s ease",
                                      textTransform: "capitalize",
                                      color: isSelected
                                        ? " #000000ff"
                                        : dark
                                          ? "#fafafaff"
                                          : "#161819",
                                    }}>
                                    {option.label}
                                  </p>

                                  {hasUserVoted && (
                                    <nav
                                      style={{
                                        width: "100%",
                                        marginTop: "-10px",
                                        animation: "fadeSlideIn 0.45s ease",
                                      }}>
                                      <div
                                        style={{
                                          width: "100%",
                                          height: "7px",
                                          borderRadius: "999px",
                                          overflow: "hidden",
                                          background: dark
                                            ? "#2c2c2cd0"
                                            : "#ddd",
                                        }}>
                                        <div
                                          style={{
                                            width: `${percent}%`,
                                            height: "100%",
                                            borderRadius: "999px",
                                            background: "#4CAF50",
                                            transition:
                                              "width 0.7s ease-in-out",
                                          }}
                                        />
                                      </div>

                                      <small
                                        style={{
                                          display: "block",
                                          marginTop: "6px",
                                          opacity: "0.8",
                                        }}>
                                        {option.percentage}% (
                                        {option.votes_count})
                                      </small>
                                    </nav>
                                  )}
                                </span>
                              );
                            })}
                          </nav>

                          {(quest.user_has_voted || selectedQuest[index]) && (
                            <strong
                              style={{
                                color: dark ? "#fff" : "#222",
                                fontSize: "18px",
                                opacity: "0.7",
                                display: "block",
                                marginTop: "0px",
                                animation: "fadeSlideIn 0.45s ease",
                              }}>
                              Total votes: {totalVotes}
                            </strong>
                          )}

                          {/* {pollLoading && <p>Voting...</p>} */}
                        </div>
                      );
                    })}
                  </section>
                )}

                {quizLoaded &&
                  relatedPosts.map(
                    (item, idx) =>
                      item.type === "quiz" &&
                      item.metadata?.quiz_questions && (
                        <div key={idx} className="quiz">
                          <h2
                            style={
                              dark
                                ? { color: "#f1f1f1ff" }
                                : { color: "#161819" }
                            }>
                            {item.title}
                          </h2>

                          {item.metadata.quiz_questions.map((q, qIndex) => {
                            const correctIndex = q.answers.findIndex(
                              (a) => a.is_correct,
                            );

                            return (
                              <div key={qIndex}>
                                <h3
                                  style={
                                    dark
                                      ? { color: "#e4e4e4ff" }
                                      : { color: "#161819" }
                                  }>
                                  {q.question}
                                </h3>

                                <nav>
                                  <img src={item.image || series} alt="" />
                                </nav>

                                {q.answers.map((ans, aIndex) => {
                                  const isSelected =
                                    quizAnswers[qIndex]?.selected === aIndex;
                                  const isCorrect = ans.is_correct;
                                  const show = showResult[qIndex];

                                  let bg = dark ? "#2c2c2cd0" : "whitesmoke";

                                  if (show) {
                                    if (isSelected && isCorrect) bg = "green";
                                    else if (isSelected && !isCorrect)
                                      bg = "red";
                                    else if (!isSelected && isCorrect)
                                      bg = "green";
                                  }

                                  return (
                                    <div
                                      key={aIndex}
                                      onClick={() => {
                                        if (!showResult[qIndex]) {
                                          handleQuizSelect(
                                            qIndex,
                                            aIndex,
                                            ans.is_correct,
                                            q.explanation,
                                          );
                                        }
                                      }}
                                      style={{
                                        alignItems: "center",
                                        padding: "10px",
                                        cursor: "pointer",
                                        color: isSelected
                                          ? "white"
                                          : show && isCorrect
                                            ? "whitesmoke"
                                            : dark
                                              ? "#888888ff"
                                              : "#3e3f3f",
                                        backgroundColor: bg,
                                        animation:
                                          show && !isSelected && isCorrect
                                            ? "blink 0.5s 2"
                                            : "none",
                                      }}>
                                      <input
                                        type="radio"
                                        name={`quiz-${qIndex}`}
                                        checked={isSelected}
                                        disabled={showResult[qIndex]}
                                        onChange={() =>
                                          handleQuizSelect(
                                            qIndex,
                                            aIndex,
                                            ans.is_correct,
                                            q.explanation,
                                          )
                                        }
                                        style={{
                                          marginRight: "10px",
                                          cursor: showResult[qIndex]
                                            ? "not-allowed"
                                            : "pointer",
                                        }}
                                      />
                                      {ans.text}
                                    </div>
                                  );
                                })}

                                {showResult[qIndex] && (
                                  <p
                                    style={{
                                      color: quizAnswers[qIndex]?.isCorrect
                                        ? "green"
                                        : "red",
                                    }}
                                    className="alert">
                                    {quizAnswers[qIndex]?.isCorrect
                                      ? "Correct!"
                                      : "Wrong!"}
                                    <br />
                                    {quizAnswers[qIndex]?.explanation}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ),
                  )}

                <section id="list">
                  {(() => {
                    const listItems = [];

                    relatedPosts.forEach((item) => {
                      if (item.type === "list" && item.metadata?.list_items) {
                        item.metadata.list_items.forEach((li, i) => {
                          listItems.push(
                            <div key={i} className="list">
                              <h3
                                style={{
                                  color: dark ? "#e4e3e3ff" : "#161819",
                                }}>
                                {i + 1}. {li.title}
                              </h3>
                              <img src={li.image} alt="" />
                              <p
                                style={{
                                  color: dark ? "#dbdbdbff" : "#161819",
                                }}>
                                {li.description}
                              </p>
                            </div>,
                          );
                        });
                      }
                    });

                    return listItems;
                  })()}
                </section>
              </section>
              <section className="firstsection">
                <nav
                  className="commenthead"
                  style={
                    dark
                      ? { backgroundColor: "#272727ff" }
                      : { backgroundColor: "whitesmoke" }
                  }>
                  <h1
                    className="title"
                    style={
                      dark
                        ? { color: "#fafafaff", marginBottom: "13px" }
                        : { color: "#161819", marginBottom: "10px" }
                    }>
                    Comments ({totalComments})
                  </h1>

                  <div
                    style={{
                      padding: replyingTo && "10px 0px 0px 0",
                      borderBottomLeftRadius: replyingTo && "15px",
                      borderBottomRightRadius: replyingTo && "15px",
                      background:
                        replyingTo &&
                        (dark
                          ? "rgba(10,10,10,0.9)"
                          : "rgba(240, 240, 240, 0.56)"),
                      backdropFilter: "blur(8px)",
                      marginBottom: "15px",
                    }}>
                    {replyingTo && (
                      <div
                        style={{
                          marginBottom: "10px",
                          fontSize: "12px",
                          color: dark ? "#aaa" : "#666",
                          display: "flex",
                          justifyContent: "space-between",
                          position: "relative",
                          width: "100%",
                          opacity: "0.8",
                        }}>
                        <span style={{ minWidth: "fit-content" }}>
                          Replying to
                          <b
                            style={{
                              marginLeft: "0px",
                              color: dark ? "#aaa" : "#464646ff",
                            }}>
                            {replyingTo?.author?.name}
                          </b>
                        </span>

                        <span
                          onClick={() => setReplyingTo(null)}
                          style={{
                            cursor: "pointer",
                            fontWeight: "700",
                            display: "flex",
                            justifyContent: "end",
                          }}>
                          Cancel
                        </span>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center",
                      }}>
                      <textarea
                        ref={textareaRef}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder={
                          replyingTo
                            ? "Write your reply..."
                            : "Write a comment..."
                        }
                        style={{
                          flex: 1,
                          resize: "none",
                          border: "none",
                          outline: "none",
                          minHeight: "38px",
                          height: "45px",
                          maxHeight: "100px",
                          borderRadius: "18px",
                          padding: "13px 15px",
                          fontSize: "14px",
                          background: dark
                            ? "rgba(255,255,255,0.05)"
                            : "#fffffff6",
                          color: dark ? "#fff" : "#111",
                        }}
                      />

                      <button
                        onClick={() => !posting && handleCommentSubmit()}
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          border: "none",
                          cursor: "pointer",
                          background: "linear-gradient(135deg,#1877f2,#005be7)",
                          color: "#fff",
                          fontSize: "18px",
                          fontWeight: "700",
                          boxShadow: "0 8px 18px rgba(24,119,242,.28)",
                          transition: ".25s ease",
                        }}>
                        ➤
                      </button>
                    </div>
                  </div>

                  {commentLoading ? (
                    <div
                      style={{
                        width: "100%",
                        maxHeight: "100px",
                        overflow: "hidden",
                      }}>
                      <Skeleton />
                    </div>
                  ) : (
                    <>
                      {comments?.slice(0, 2)?.map((comment, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            gap: "6px",
                            marginBottom: "18px",
                          }}>
                          <div className="author-initials">
                            {getInitials(comment?.author?.name)}
                          </div>

                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                padding: "12px 14px",
                                borderRadius: "18px",
                                gap: "2px",
                                background: dark
                                  ? "rgba(255,255,255,0.04)"
                                  : "#ffffff73",
                              }}>
                              <h4
                                style={{
                                  margin: 0,
                                  fontSize: "17px",
                                  fontWeight: "700",
                                  color: dark ? "#fff" : "#111",
                                }}>
                                {comment?.author?.name}
                              </h4>

                              <p
                                style={{
                                  marginTop: "6px",
                                  lineHeight: "1.45",
                                  fontSize: "15px",
                                  maxWidth: "100%",
                                  color: dark ? "#ddd" : "#444",
                                  whiteSpace: "pre-wrap",
                                }}>
                                {comment?.content}
                              </p>
                            </div>

                            <div
                              id="repl"
                              style={{
                                fontSize: "13px",
                                color: dark ? "#9e9e9e" : "#666",
                                fontWeight: "500",
                              }}>
                              <span
                                style={{
                                  minWidth: "fit-content",
                                  maxWidth: "22%",
                                }}>
                                {formatTimeAgo(comment?.created_at)}
                              </span>

                              <nav className="replik">
                                <p
                                  style={
                                    dark
                                      ? { color: "#fafafaff", fontSize: "14px" }
                                      : { color: "#7F7F81", fontSize: "14px" }
                                  }>
                                  {comment?.likes_count || 0}
                                </p>
                                <span
                                  className="like"
                                  onClick={() => handleLike(comment)}
                                  style={{
                                    cursor: "pointer",
                                    userSelect: "none",
                                    color: comment.liked_by_user
                                      ? "#fff"
                                      : dark
                                        ? "#ddd"
                                        : "#444",
                                    transition: "0.25s ease",
                                    width: "20px",
                                    height: "20px",
                                  }}>
                                  <img
                                    src={like}
                                    alt=""
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      filter: comment.liked_by_user
                                        ? "invert(12%) sepia(96%) saturate(2870%) hue-rotate(199deg) brightness(85%) contrast(98%)"
                                        : dark
                                          ? "invert(80%)"
                                          : "invert(20%)",
                                      transition: "0.25s ease",
                                    }}
                                  />
                                </span>
                                <b
                                  style={
                                    dark
                                      ? { color: "#fafafaff", fontSize: "14px" }
                                      : {
                                          color: "#7F7F81",
                                          fontSize: "18px",
                                          display: "flex",
                                          justifyContent: "center",
                                          alignItems: "center",
                                          marginTop: "-6px",
                                        }
                                  }>
                                  .
                                </b>

                                <span
                                  onClick={() => {
                                    setReplyingTo(comment);

                                    setVisibleReplies((prev) => ({
                                      ...prev,
                                      [comment.id]: 2,
                                    }));
                                    setTimeout(() => {
                                      textareaRef.current?.focus();
                                    }, 150);
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    transition: ".2s",
                                    zIndex: "1000",
                                  }}>
                                  Reply ({(replies[comment.id] || []).length})
                                </span>
                              </nav>

                              <div
                                className="myreply"
                                style={{
                                  marginTop: "15px",
                                  marginBottom: "14px",
                                  display: "flex",
                                  flexDirection: "column-reverse",
                                  gap: "0px",
                                  margin: "0",
                                  minWidth: "100%",
                                  // transform: "translateX(-68%)",
                                  zIndex: "0",
                                }}>
                                <div>
                                  {(replies[comment.id] || []).length > 0 &&
                                    !visibleReplies[comment.id] && (
                                      <button
                                        onClick={() =>
                                          setVisibleReplies((prev) => ({
                                            ...prev,
                                            [comment.id]: 2,
                                          }))
                                        }
                                        style={{
                                          border: "none",
                                          background: "transparent",
                                          cursor: "pointer",
                                          fontSize: "13px",
                                          fontWeight: "600",
                                          marginLeft: "20px",
                                          marginTop: "20px",
                                          opacity: "0.6",
                                          color: dark ? "#fff" : "#4e565fff",
                                        }}>
                                        ____View Replies
                                        {/* ({(replies[comment.id] || []).length}) */}
                                      </button>
                                    )}

                                  {(replies[comment.id] || [])
                                    .slice(0, visibleReplies[comment.id] || 0)
                                    .map((rep, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          display: "flex",
                                          gap: "10px",
                                          flexDirection: "column",
                                          margin: "0",
                                        }}>
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "5px",
                                            marginTop: "20px",
                                            marginLeft: "18px",
                                            alignItems: "flex-start",
                                            position: "relative",
                                            minwidth: "100%",
                                          }}>
                                          <span
                                            style={{
                                              position: "absolute",
                                              left: "-12px",
                                              top: "0",
                                              bottom: "0",
                                              width: "2px",
                                              borderRadius: "10px",
                                            }}
                                          />

                                          <div className="author-initials">
                                            {getInitials(rep?.author?.name)}
                                          </div>

                                          <div
                                            className="replycontent"
                                            style={{
                                              flex: 1,
                                              borderRadius: "16px",
                                              background: dark
                                                ? "linear-gradient(135deg,#181818,#111)"
                                                : "#ffffff6c",
                                              transition: "0.25s ease",
                                            }}>
                                            <span
                                              style={{
                                                display: "flex",
                                                flexDirection: "row",
                                                justifyContent: "start",
                                                alignItems: "center",
                                                gap: "8px",
                                                flexWrap: "wrap",
                                              }}>
                                              <h4
                                                style={{
                                                  margin: 0,
                                                  fontSize: "14px",
                                                  fontWeight: "500",
                                                  letterSpacing: ".3px",
                                                  color: dark ? "#fff" : "#111",
                                                  textTransform: "capitalize",
                                                }}>
                                                {rep?.author?.name}
                                              </h4>
                                            </span>

                                            <span
                                              style={{
                                                display: "flex",
                                                justifyContent: "start",
                                                alignItems: "center",
                                                gap: "6px",
                                                flexWrap: "wrap",
                                              }}>
                                              {rep?.mention && (
                                                <div
                                                  style={{
                                                    flexDirection: "row",
                                                    marginTop: "6px",
                                                    fontSize: "14px",
                                                    fontWeight: "600",
                                                  }}>
                                                  @{rep?.mention}
                                                </div>
                                              )}

                                              <p
                                                style={{
                                                  marginTop: "6px",
                                                  marginBottom: "0",
                                                  fontSize: "15px",
                                                  lineHeight: "1.5",
                                                  color: dark
                                                    ? "#e7e7e7"
                                                    : "#222",
                                                  whiteSpace: "pre-wrap",
                                                }}>
                                                {rep?.content}
                                              </p>
                                            </span>
                                          </div>
                                        </div>

                                        <small
                                          style={{
                                            fontSize: "11px",
                                            color: dark ? "#9d9d9d" : "#777",
                                            fontWeight: "600",
                                            margin: "0px 0 0px 70px",
                                          }}>
                                          {formatTimeAgo(rep?.created_at)}
                                        </small>
                                      </div>
                                    ))}

                                  {(replies[comment.id] || []).length >
                                    (visibleReplies[comment.id] || 0) &&
                                    visibleReplies[comment.id] > 0 && (
                                      <button
                                        onClick={() =>
                                          setVisibleReplies((prev) => ({
                                            ...prev,
                                            [comment.id]:
                                              (prev[comment.id] || 0) + 2,
                                          }))
                                        }
                                        style={{
                                          border: "none",
                                          background: "transparent",
                                          cursor: "pointer",
                                          fontSize: "13px",
                                          fontWeight: "600",
                                          marginLeft: "20px",
                                          opacity: "0.6",
                                          marginTop: "12px",
                                          color: dark ? "#fff" : "#4e565fff",
                                        }}>
                                        ___Show older replies
                                      </button>
                                    )}

                                  {visibleReplies[comment.id] >=
                                    (replies[comment.id] || []).length &&
                                    (replies[comment.id] || []).length > 0 && (
                                      <button
                                        onClick={() =>
                                          setVisibleReplies((prev) => ({
                                            ...prev,
                                            [comment.id]: 0,
                                          }))
                                        }
                                        style={{
                                          border: "none",
                                          background: "transparent",
                                          cursor: "pointer",
                                          fontSize: "13px",
                                          fontWeight: "600",
                                          marginLeft: "20px",
                                          opacity: "0.6",
                                          marginTop: "12px",
                                          color: dark ? "#fff" : "#161616ff",
                                        }}>
                                        ___Hide Replies
                                      </button>
                                    )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      <div
                        className="ment"
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "14px",
                        }}>
                        {comments.length <= 0 && (
                          <strong
                            style={{
                              fontSize: "14px",
                              opacity: "0.6",
                              color: dark ? "#cacacaff" : "#3e3f3f",
                              textAlign: "center",
                              width: "100%",
                            }}>
                            No comments, be the first to comment
                          </strong>
                        )}

                        {totalComments > 2 && (
                          <button
                            onClick={() => setCommentModal(true)}
                            style={{
                              width: "100%",
                              padding: "13px 15px",
                              border: "none",
                              borderRadius: "15px",
                              cursor: "pointer",
                              opacity: "0.9",
                              opacity: "0.7",
                              backgroundColor: "transparent",
                              color: dark ? "#fff" : "#111",
                              fontWeight: "600",
                            }}>
                            View All Comments ({totalComments})
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </nav>

                {commentModal && (
                  <div
                    onClick={closeCommentModal}
                    style={{
                      width: "100%",
                      height: "100vh",
                      position: "fixed",
                      bottom: "0",
                      left: "0",
                      background: "#00000021",
                      backdropFilter: "blur(0.6px)",
                      WebkitBackdropFilter: "blur(6px)",
                      zIndex: 10000,
                      display: "flex",
                      alignItems: "flex-end",
                      animation: "fadeIn .4s ease",
                    }}
                    className="commentmodal">
                    <div
                      ref={modalSheetRef}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => startDragSheet(e.clientY)}
                      onMouseMove={(e) => moveDragSheet(e.clientY)}
                      onMouseUp={endDragSheet}
                      onMouseLeave={endDragSheet}
                      onTouchStart={(e) => startDragSheet(e.touches[0].clientY)}
                      onTouchMove={(e) => moveDragSheet(e.touches[0].clientY)}
                      onTouchEnd={endDragSheet}
                      style={{
                        width: "100%",
                        minHeight: "70vh",
                        maxHeight: "70vh",
                        background: dark
                          ? "linear-gradient(180deg,#141414,#0d0d0d)"
                          : "linear-gradient(180deg,#ffffff,#f8f8f8)",
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        boxShadow: "0 -10px 35px rgba(0, 0, 0, 0.1)",
                        animation: closingModal
                          ? "slideDown .58s cubic-bezier(.22,1,.36,1) forwards"
                          : "slideUp .58s cubic-bezier(.22,1,.36,1) forwards",
                      }}>
                      <div
                        style={{
                          position: "relative",
                          padding: "11px 14px",
                          borderBottom: dark
                            ? "1px solid rgba(255,255,255,0.06)"
                            : "1px solid rgba(0,0,0,0.06)",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}>
                        <div
                          style={{
                            position: "absolute",
                            top: "8px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "52px",
                            height: "5px",
                            borderRadius: "20px",
                            background: dark
                              ? "rgba(255,255,255,0.18)"
                              : "rgba(0,0,0,0.15)",
                          }}
                        />

                        <h3
                          style={{
                            margin: 0,
                            fontSize: "17px",
                            opacity: "0.6",
                            fontWeight: "600",
                            letterSpacing: "-0.2px",
                            color: dark ? "#fff" : "#111",
                          }}>
                          Comments ({totalComments})
                        </h3>

                        <button
                          onClick={closeCommentModal}
                          style={{
                            width: "32px",
                            height: "32px",
                            opacity: "0.9",
                            borderRadius: "50%",
                            border: "none",
                            outline: "none",
                            cursor: "pointer",
                            background: dark
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(0,0,0,0.05)",
                            color: dark ? "#fff" : "#111",
                            fontSize: "18px",
                            fontWeight: "700",
                            transition: "0.25s ease",
                          }}>
                          ✕
                        </button>
                      </div>

                      <div
                        style={{
                          flex: 1,
                          overflowY: "auto",
                          padding: "15px",
                          scrollBehavior: "smooth",
                        }}>
                        {comments.length <= 0 && (
                          <p
                            style={{
                              fontSize: "16px",
                              opacity: "0.6",
                              color: dark ? "#cacacaff" : "#3e3f3f",
                              textAlign: "center",
                            }}>
                            No comments, be the first to comment
                          </p>
                        )}
                        {comments.map((comment, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              gap: "6px",
                              marginBottom: "18px",
                            }}>
                            <img
                              src={comment?.author?.image || hall}
                              alt=""
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "50%",
                                objectFit: "cover",
                                marginTop: "10px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                              }}
                            />

                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  padding: "12px 14px",
                                  borderRadius: "18px",
                                  gap: "2px",
                                  background: dark
                                    ? "rgba(255,255,255,0.04)"
                                    : "#ffffff73",
                                }}>
                                <h4
                                  style={{
                                    margin: 0,
                                    fontSize: "17px",
                                    fontWeight: "700",
                                    color: dark ? "#fff" : "#111",
                                  }}>
                                  {comment?.author?.name}
                                </h4>

                                <p
                                  style={{
                                    marginTop: "6px",
                                    lineHeight: "1.45",
                                    fontSize: "15px",
                                    maxWidth: "100%",
                                    color: dark ? "#ddd" : "#444",
                                  }}>
                                  {comment?.content}
                                </p>
                              </div>

                              <div
                                id="repl"
                                style={{
                                  fontSize: "13px",
                                  color: dark ? "#9e9e9e" : "#666",
                                  fontWeight: "500",
                                }}>
                                <span
                                  style={{
                                    minWidth: "fit-content",
                                    maxWidth: "22%",
                                  }}>
                                  {formatTimeAgo(comment?.created_at)}
                                </span>

                                <nav className="replik">
                                  <p
                                    style={
                                      dark
                                        ? {
                                            color: "#fafafaff",
                                            fontSize: "14px",
                                          }
                                        : { color: "#7F7F81", fontSize: "14px" }
                                    }>
                                    {comment.likes_count || 0}
                                  </p>
                                  <span
                                    className="like"
                                    onClick={() => handleLike(comment)}
                                    style={{
                                      cursor: "pointer",
                                      userSelect: "none",
                                      color: comment.liked_by_user
                                        ? "#fff"
                                        : dark
                                          ? "#ddd"
                                          : "#444",
                                      transition: "0.25s ease",
                                      width: "20px",
                                      height: "20px",
                                    }}>
                                    <img
                                      src={like}
                                      alt=""
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        filter: comment.liked_by_user
                                          ? "invert(12%) sepia(96%) saturate(2870%) hue-rotate(199deg) brightness(85%) contrast(98%)"
                                          : dark
                                            ? "invert(80%)"
                                            : "invert(20%)",
                                        transition: "0.25s ease",
                                      }}
                                    />
                                  </span>
                                  <b
                                    style={
                                      dark
                                        ? {
                                            color: "#fafafaff",
                                            fontSize: "14px",
                                          }
                                        : {
                                            color: "#7F7F81",
                                            fontSize: "18px",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginTop: "-6px",
                                          }
                                    }>
                                    .
                                  </b>

                                  <span
                                    onClick={() => {
                                      setReplyingTo(comment);

                                      setVisibleReplies((prev) => ({
                                        ...prev,
                                        [comment.id]: 2,
                                      }));
                                      setTimeout(() => {
                                        textareaRef.current?.focus();
                                      }, 150);
                                    }}
                                    style={{
                                      cursor: "pointer",
                                      transition: ".2s",
                                    }}>
                                    Reply ({(replies[comment.id] || []).length})
                                  </span>
                                </nav>

                                <div
                                  className="myreply"
                                  style={{
                                    marginTop: "15px",
                                    marginBottom: "14px",
                                    display: "flex",
                                    flexDirection: "column-reverse",
                                    gap: "0px",
                                    margin: "0",
                                    minWidth: "100%",
                                    zIndex: "0",
                                  }}>
                                  <div>
                                    {(replies[comment.id] || []).length > 0 &&
                                      !visibleReplies[comment.id] && (
                                        <button
                                          onClick={() =>
                                            setVisibleReplies((prev) => ({
                                              ...prev,
                                              [comment.id]: 2,
                                            }))
                                          }
                                          style={{
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            fontSize: "13px",
                                            fontWeight: "600",
                                            marginLeft: "20px",
                                            marginTop: "20px",
                                            opacity: "0.6",
                                            color: dark ? "#fff" : "#4e565fff",
                                          }}>
                                          ____View Replies
                                          {/* ({(replies[comment.id] || []).length}) */}
                                        </button>
                                      )}

                                    {(replies[comment.id] || [])
                                      .slice(0, visibleReplies[comment.id] || 0)
                                      .map((rep, i) => (
                                        <div
                                          key={i}
                                          style={{
                                            display: "flex",
                                            gap: "10px",
                                            flexDirection: "column",
                                            margin: "0",
                                          }}>
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: "5px",
                                              marginTop: "20px",
                                              marginLeft: "18px",
                                              alignItems: "flex-start",
                                              position: "relative",
                                              minwidth: "100%",
                                            }}>
                                            <span
                                              style={{
                                                position: "absolute",
                                                left: "-12px",
                                                top: "0",
                                                bottom: "0",
                                                width: "2px",
                                                borderRadius: "10px",
                                              }}
                                            />

                                            <img
                                              src={rep?.author?.image || hall}
                                              alt=""
                                              style={{
                                                width: "30px",
                                                height: "30px",
                                                borderRadius: "50%",
                                                objectFit: "cover",
                                                border: dark
                                                  ? "1px solid rgba(255,255,255,0.08)"
                                                  : "1px solid rgba(0,0,0,0.08)",
                                                boxShadow:
                                                  "0 4px 10px rgba(0,0,0,0.12)",
                                                flexShrink: 0,
                                              }}
                                            />

                                            <div
                                              className="replycontent"
                                              style={{
                                                flex: 1,
                                                borderRadius: "16px",
                                                background: dark
                                                  ? "linear-gradient(135deg,#181818,#111)"
                                                  : "#ffffff6c",
                                                transition: "0.25s ease",
                                              }}>
                                              <span
                                                style={{
                                                  display: "flex",
                                                  flexDirection: "row",
                                                  justifyContent: "start",
                                                  alignItems: "center",
                                                  gap: "8px",
                                                  flexWrap: "wrap",
                                                }}>
                                                <h4
                                                  style={{
                                                    margin: 0,
                                                    fontSize: "14px",
                                                    fontWeight: "500",
                                                    letterSpacing: ".3px",
                                                    color: dark
                                                      ? "#fff"
                                                      : "#111",
                                                    textTransform: "capitalize",
                                                  }}>
                                                  {rep?.author?.name}
                                                </h4>
                                              </span>

                                              <span
                                                style={{
                                                  display: "flex",
                                                  justifyContent: "start",
                                                  alignItems: "center",
                                                  gap: "6px",
                                                  flexWrap: "wrap",
                                                }}>
                                                {rep?.mention && (
                                                  <div
                                                    style={{
                                                      flexDirection: "row",
                                                      marginTop: "6px",
                                                      fontSize: "14px",
                                                      fontWeight: "600",
                                                    }}>
                                                    @{rep?.mention}
                                                  </div>
                                                )}

                                                <p
                                                  style={{
                                                    marginTop: "6px",
                                                    lineHeight: "1.45",
                                                    fontSize: "15px",
                                                    maxWidth: "100%",
                                                    color: dark
                                                      ? "#ddd"
                                                      : "#444",
                                                    whiteSpace: "pre-wrap",
                                                  }}>
                                                  {rep?.comment?.content}
                                                </p>
                                              </span>
                                            </div>
                                          </div>

                                          <small
                                            style={{
                                              fontSize: "11px",
                                              color: dark ? "#9d9d9d" : "#777",
                                              fontWeight: "600",
                                              margin: "0px 0 0px 70px",
                                            }}>
                                            {formatTimeAgo(rep?.created_at)}
                                          </small>
                                        </div>
                                      ))}

                                    {(replies[comment.id] || []).length >
                                      (visibleReplies[comment.id] || 0) &&
                                      visibleReplies[comment.id] > 0 && (
                                        <button
                                          onClick={() =>
                                            setVisibleReplies((prev) => ({
                                              ...prev,
                                              [comment.id]:
                                                (prev[comment.id] || 0) + 2,
                                            }))
                                          }
                                          style={{
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            fontSize: "13px",
                                            fontWeight: "600",
                                            marginLeft: "20px",
                                            opacity: "0.6",
                                            marginTop: "12px",
                                            color: dark ? "#fff" : "#4e565fff",
                                          }}>
                                          ___Show older replies
                                        </button>
                                      )}

                                    {visibleReplies[comment.id] >=
                                      (replies[comment.id] || []).length &&
                                      (replies[comment.id] || []).length >
                                        0 && (
                                        <button
                                          onClick={() =>
                                            setVisibleReplies((prev) => ({
                                              ...prev,
                                              [comment.id]: 0,
                                            }))
                                          }
                                          style={{
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            fontSize: "13px",
                                            fontWeight: "600",
                                            marginLeft: "20px",
                                            opacity: "0.6",
                                            marginTop: "12px",
                                            color: dark ? "#fff" : "#161616ff",
                                          }}>
                                          ___Hide Replies
                                        </button>
                                      )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                        {commentLoading && (
                          <div
                            style={{
                              textAlign: "center",
                              padding: "0px",
                              color: dark ? "#d3d3d3ff" : "#3e3f3f",
                              opacity: "0.7",
                              fontSize: "0px",
                            }}></div>
                        )}

                        {commentPage < commentLastPage &&
                          commentPage < visiblePageLimit && (
                            <div
                              style={{
                                textAlign: "center",
                                marginTop: "10px",
                              }}>
                              <button
                                onClick={loadNextPage}
                                style={{
                                  border: "none",
                                  padding: "10px 18px",
                                  borderRadius: "12px",
                                  cursor: "pointer",
                                  background: "transparent",
                                  color: !dark ? "#5f5f5f80" : "#e7e7e7ff",
                                  fontWeight: "400",
                                  fontSize: "15px",
                                }}>
                                see more comments
                              </button>
                            </div>
                          )}

                        {commentPage >= visiblePageLimit &&
                          commentPage < commentLastPage && (
                            <div
                              style={{
                                textAlign: "center",
                                marginTop: "10px",
                              }}>
                              <button
                                onClick={handleSeeMorePages}
                                style={{
                                  border: "none",
                                  padding: "10px 18px",
                                  borderRadius: "12px",
                                  cursor: "pointer",
                                  background: dark ? "#222" : "#f1f1f1",
                                  color: dark ? "#fff" : "#111",
                                  fontWeight: "600",
                                }}>
                                See More
                              </button>
                            </div>
                          )}
                      </div>

                      <div
                        style={{
                          padding: "10px 16px",
                          borderTop: dark
                            ? "1px solid rgba(255,255,255,0.05)"
                            : "1px solid rgba(0,0,0,0.06)",
                          background: dark
                            ? "rgba(10,10,10,0.9)"
                            : "rgba(255,255,255,0.92)",
                          backdropFilter: "blur(8px)",
                        }}>
                        {replyingTo && (
                          <div
                            style={{
                              marginBottom: "10px",
                              fontSize: "12px",
                              color: dark ? "#aaa" : "#666",
                              display: "flex",
                              justifyContent: "space-between",
                              position: "relative",
                              width: "100%",
                            }}>
                            <span style={{ minWidth: "fit-content" }}>
                              Replying to
                              <b
                                style={{
                                  marginLeft: "0px",
                                  color: dark ? "#aaa" : "#464646ff",
                                }}>
                                {replyingTo?.author?.name}
                              </b>
                            </span>

                            <span
                              onClick={() => setReplyingTo(null)}
                              style={{
                                cursor: "pointer",
                                fontWeight: "700",
                                display: "flex",
                                justifyContent: "end",
                              }}>
                              Cancel
                            </span>
                          </div>
                        )}

                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                          }}>
                          <textarea
                            ref={textareaRef}
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder={
                              replyingTo
                                ? "Write your reply..."
                                : "Write a comment..."
                            }
                            style={{
                              flex: 1,
                              resize: "none",
                              border: "none",
                              outline: "none",
                              minHeight: "38px",
                              height: "45px",
                              maxHeight: "100px",
                              borderRadius: "18px",
                              padding: "13px 15px",
                              fontSize: "14px",
                              background: dark
                                ? "rgba(255,255,255,0.05)"
                                : "#f3f4f6",
                              color: dark ? "#fff" : "#111",
                            }}
                          />

                          <button
                            onClick={() => !posting && handleCommentSubmit()}
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              border: "none",
                              cursor: "pointer",
                              background:
                                "linear-gradient(135deg,#1877f2,#005be7)",
                              color: "#fff",
                              fontSize: "18px",
                              fontWeight: "700",
                              boxShadow: "0 8px 18px rgba(24,119,242,.28)",
                              transition: ".25s ease",
                            }}>
                            ➤
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            </section>

            <nav className="navbar">
              {relatedPosts.length > 0 && (
                <h1
                  className="title"
                  style={dark ? { color: "#fafafaff" } : { color: "#161819" }}>
                  Related{" "}
                </h1>
              )}

              <div className="trendingcontent">
                {(() => {
                  const items = [];

                  relatedPosts.slice(0, 5).forEach((related, index) => {
                    items.push(
                      <Link to={`/news/${related?.slug}`} className="new">
                        <div
                          className="trends"
                          key={index}
                          style={
                            dark
                              ? { backgroundColor: "#333333ff" }
                              : { backgroundColor: "#ffffffff" }
                          }>
                          <div className="imgdiv4">
                            <img src={related.image || hall} alt="" />
                            <span>{index + 1}</span>
                          </div>
                          <p
                            style={
                              dark
                                ? { color: "#f3f3f3ff" }
                                : { color: "#161819" }
                            }>
                            {related.title}
                          </p>
                        </div>
                      </Link>,
                    );
                  });

                  return items;
                })()}
              </div>
            </nav>
          </div>

          {/* <section className="fourthsection">
                {
                    relatedPosts.slice(0, 5).map((item, index) => (
                        <div key={index}>
                            <nav>
                                <img src={item.image || series} alt="" />
                            </nav>
                            <nav className="con">
                                <h3>{item.type}</h3>
                                <h2>{item.title}</h2>
                                <p>{item.excerpt}</p>
                            </nav>
                        </div>
                    ))
                }
            </section> */}
        </div>
      )}

      {showLoginModal && (
        <div
          className="commentmodal"
          // onClick={() => setShowLoginModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.06)",
            display: "flex",
            alignItems: "end",
            justifyContent: "center",
            zIndex: 99999,
            padding: "4% 6%",
          }}>
          <div
            // onClick={(e) => e.stopPropagation()}
            style={{
              background: dark ? "#1d1d1d" : "#fff",
              padding: "20px",
              borderRadius: "12px",
              textAlign: "center",
              width: "100%",
            }}>
            <p
              style={{
                color: dark ? "#fafafaff" : "#3e3f3f",
                fontSize: "15px",
              }}>
              Log in to interact with the community — like, comment, and join
              replies
            </p>
            <nav
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <button
                onClick={() => setShowLoginModal(false)}
                style={{
                  display: "inline-block",
                  marginTop: "10px",
                  padding: "8px 14px",
                  background: "#d1d1d1ff",
                  color: "#3e3f3f",
                  borderRadius: "8px",
                  border: "none",
                  outline: "none",
                  textDecoration: "none",
                }}>
                Cancel
              </button>

              <NavLink
                to="/login"
                state={{ from: location }}
                onClick={() => setShowLoginModal(false)}
                style={{
                  display: "inline-block",
                  marginTop: "10px",
                  padding: "8px 14px",
                  background: "#1e63ff",
                  color: "#fff",
                  borderRadius: "8px",
                  textDecoration: "none",
                  fontSize: "14px",
                }}>
                Continue
              </NavLink>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default Review;
